import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';

const ViewLinkModal = ({ isOpen, onClose, linkId }) => {
  const { currentLang, token, API_URL, getCsrfToken } = useAuth();
  
  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Загружаем данные ссылки при открытии модалки
  useEffect(() => {
    if (isOpen && linkId) {
      fetchLinkData();
    }
  }, [isOpen, linkId]);

  // Управляем прокруткой body при открытии/закрытии модалки
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const fetchLinkData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetch(`${API_URL}${currentLang}/links/${linkId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Декодируем HTML из Base64 и исправляем кодировку UTF-8
        let decodedHtml = '';
        if (data.link.html) {
          try {
            const base64Decoded = atob(data.link.html);
            const utf8Decoded = decodeURIComponent(escape(base64Decoded));
            decodedHtml = utf8Decoded;
          } catch (error) {
            console.error('Ошибка декодирования HTML:', error);
            try {
              const base64Decoded = atob(data.link.html);
              decodedHtml = base64Decoded;
            } catch (secondError) {
              console.error('Вторая попытка декодирования не удалась:', secondError);
              decodedHtml = data.link.html;
            }
          }
        }

        setLinkData({
          ...data.link,
          html: decodedHtml
        });
      } else {
        throw new Error(data.message || 'Ошибка загрузки данных ссылки');
      }
    } catch (error) {
      console.error('Ошибка загрузки данных ссылки:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch (error) {
      return dateString;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 0: return currentLang === 'ru' ? 'Ожидает проверки' : 'Pending';
      case 1: return currentLang === 'ru' ? 'Активная' : 'Active';
      case 2: return currentLang === 'ru' ? 'Не найдена' : 'Not found';
      case 99: return currentLang === 'ru' ? 'Снята' : 'Removed';
      default: return '-';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 0: return 'bg-yellow-100 text-yellow-800';
      case 1: return 'bg-green-100 text-green-800';
      case 2: return 'bg-red-100 text-red-800';
      case 99: return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getLangColor = (lang) => {
    switch (lang) {
      case 'ru': return 'bg-blue-100 text-blue-800';
      case 'en': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">
            {currentLang === 'ru' ? 'Просмотр ссылки' : 'View Link'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
          </div>
        ) : error ? (
          <div className="p-4 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        ) : linkData ? (
          <div className="space-y-4">

            {/* Статус */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {currentLang === 'ru' ? 'Статус' : 'Status'}
                <span className={`ml-2 px-2 py-1 text-xs font-bold rounded-full ${getStatusColor(linkData.status)}`}>
                  {getStatusText(linkData.status)}
                </span>
              </label>
            </div>

            {/* Язык */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {currentLang === 'ru' ? 'Язык' : 'Language'}  
                <span className={`ml-2 px-2 py-1 text-xs font-bold rounded-full ${getLangColor(linkData.lang)}`}>
                  {linkData.lang?.toUpperCase() || '-'}
                </span>
              </label>
              
            </div>

            {/* Теги */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {currentLang === 'ru' ? 'Теги' : 'Tags'}
                {linkData.td ? (
                  <span className="ml-2 inline-flex flex-wrap gap-1">
                    {linkData.td.split(',').map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                      >
                        {tag.trim()}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span className="text-gray-500">-</span>
                )}
              </label>
            </div>

            {/* Пользователь */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {currentLang === 'ru' ? 'Пользователь' : 'User'}
                <span className="ml-2 px-2 py-1 text-xs font-bold rounded-full bg-blue-100 text-blue-800">
                  {linkData.user_name || 'Unknown'}
                </span>
              </label>
              
            </div>

            {/* URL акцептора */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {currentLang === 'ru' ? 'URL акцептора' : 'Acceptor URL'}
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md break-all">
                <a 
                  href={linkData.aurl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  {linkData.aurl}
                </a>
              </div>
            </div>

            {/* URL донора */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {currentLang === 'ru' ? 'URL донора' : 'Donor URL'}
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md break-all">
                <a 
                  href={linkData.durl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  {linkData.durl}
                </a>
              </div>
            </div>

            {/* Дата создания */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {currentLang === 'ru' ? 'Дата создания' : 'Creation Date'}
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                {formatDate(linkData.dat)}
              </div>
            </div>

            {/* Последняя проверка */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {currentLang === 'ru' ? 'Последняя проверка' : 'Last Check'}
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                {formatDate(linkData.dend)}
              </div>
            </div>

            {/* SEO параметры */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {currentLang === 'ru' ? 'SEO параметры' : 'SEO Parameters'}
              </label>
              <div className="space-y-2">
                {/* Количество ссылок */}
                <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                  <span className="text-sm font-medium text-gray-700">
                    {currentLang === 'ru' ? 'Количество ссылок:' : 'Number of links:'}
                  </span>
                  <span className="ml-2 text-sm text-gray-900">
                    {linkData.stat_links || '-'}
                  </span>
                </div>
                
                {/* Позиция */}
                <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                  <span className="text-sm font-medium text-gray-700">
                    {currentLang === 'ru' ? 'Позиция:' : 'Position:'}
                  </span>
                  <span className="ml-2 text-sm text-gray-900">
                    {linkData.stat_pos || '-'}
                  </span>
                </div>
                
                {/* Атрибуты */}
                <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                  <span className="text-sm font-medium text-gray-700">
                    {currentLang === 'ru' ? 'Атрибуты:' : 'Attributes:'}
                  </span>
                  <div className="mt-1">
                    {linkData.stat_attr ? (
                      <div className="flex flex-wrap gap-1">
                        {linkData.stat_attr.split(',').map((attr, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full"
                          >
                            {attr.trim()}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">-</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* HTML код */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                HTML код ссылки
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md font-mono text-sm">
                <pre className="whitespace-pre-wrap break-words">
                  {linkData.html || 'HTML код не найден'}
                </pre>
              </div>
            </div>
          </div>
        ) : null}

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {currentLang === 'ru' ? 'Закрыть' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewLinkModal; 